import React from "react";
import { ArrowRight, ArrowUpRight, Check, Phone } from "lucide-react";
import { Link } from "wouter";
import { GoogleReviewsCarousel } from "@/components/GoogleReviewsCarousel";
import { StructuredData } from "@/components/StructuredData";
import { assets, materials, realPhotos, siteConfig, siteIdentityLinks } from "@/data/site";

const accessoryHighlights = [
  { name: "Grab Bars", href: "/commercial-restroom-accessories/accessibility-safety", photo: realPhotos.accessories.grabBars, alt: "Commercial restroom grab bars" },
  { name: "Hand Dryers", href: "/commercial-restroom-accessories/handwashing-hygiene", photo: realPhotos.accessories.handDryers, alt: "Commercial restroom hand dryers" },
  { name: "Mirrors", href: "/commercial-restroom-accessories/mirrors-storage", photo: realPhotos.accessories.mirrors, alt: "Commercial restroom mirrors" },
  { name: "Paper Holders & Dispensers", href: "/commercial-restroom-accessories/paper-waste", photo: realPhotos.accessories.paperDispensers, alt: "Commercial restroom paper dispensers" },
];

export default function Home() {
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": ["Organization", "LocalBusiness"],
    "@id": `${siteConfig.url}/#business`,
    name: siteConfig.name,
    legalName: siteConfig.legalName,
    url: siteConfig.url,
    logo: `${siteConfig.url}${assets.logo}`,
    image: `${siteConfig.url}${realPhotos.hero.large}`,
    telephone: siteConfig.phoneHref,
    email: siteConfig.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: siteConfig.address.streetAddress,
      addressLocality: siteConfig.address.addressLocality,
      addressRegion: siteConfig.address.addressRegion,
      postalCode: siteConfig.address.postalCode,
      addressCountry: siteConfig.address.addressCountry,
    },
    description: siteConfig.description,
    sameAs: siteIdentityLinks,
    hasMap: siteConfig.address.googleBusinessProfile,
    areaServed: { "@type": "Country", name: "United States" },
    contactPoint: { "@type": "ContactPoint", telephone: siteConfig.phoneHref, email: siteConfig.email, contactType: "sales", areaServed: "US", availableLanguage: "English" },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Commercial Bathroom Partitions and Accessories",
      itemListElement: materials.map((material) => ({ "@type": "OfferCatalog", name: material.name, url: `${siteConfig.url}/bathroom-partitions/${material.slug}` })),
    },
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${siteConfig.url}/#website`,
    url: siteConfig.url,
    name: siteConfig.name,
    description: siteConfig.description,
    publisher: { "@id": `${siteConfig.url}/#business` },
    inLanguage: "en-US",
  };

  return (
    <>
      <StructuredData data={[organizationSchema, websiteSchema]} />

      <section className="simple-hero simple-hero--concise section-line" aria-labelledby="hero-title">
        <div className="simple-hero__copy">
          <div className="hero__eyebrow"><span className="red-square" aria-hidden="true" /> Commercial restroom supply · Nationwide</div>
          <h1 id="hero-title" className="simple-hero__title">
            <span className="simple-hero__title-line">Commercial</span>{" "}
            <span className="simple-hero__title-line">toilet</span>{" "}
            <span className="simple-hero__title-line simple-hero__title-line--split">
              <span>partitions</span>{" "}
              <span className="simple-hero__title-highlight">made</span>
            </span>{" "}
            <span className="simple-hero__title-line simple-hero__title-highlight simple-hero__title-highlight--simple">simple.</span>
          </h1>
          <p>Your commercial toilet partition supplier for new bathroom stalls, restroom renovations, and accessories. Compare five materials, then send plans, measurements, photos, or a product list for a focused quote.</p>
          <div className="button-row">
            <Link href="/request-a-quote" className="button button--primary">Request a Quote <ArrowUpRight aria-hidden="true" /></Link>
            <a href={`tel:${siteConfig.phoneHref}`} className="button button--outline"><Phone aria-hidden="true" /> Call {siteConfig.phoneDisplay}</a>
          </div>
          <ul className="simple-proof" aria-label="Partition Stalls service highlights">
            <li><Check aria-hidden="true" /> Five partition materials</li>
            <li><Check aria-hidden="true" /> Accessories and hardware</li>
            <li><Check aria-hidden="true" /> Nationwide delivery support</li>
          </ul>
          <Link href="/projects" className="simple-hero__projects-link">View documented projects <ArrowRight aria-hidden="true" /></Link>
        </div>
        <figure className="simple-hero__photo">
          <img src={realPhotos.hero.large} srcSet={`${realPhotos.hero.small} 720w, ${realPhotos.hero.medium} 1200w, ${realPhotos.hero.large} 1600w`} sizes="(max-width: 800px) 100vw, 52vw" width="1600" height="1074" alt="Finished commercial restroom with wood-look bathroom partitions" fetchPriority="high" />
          <figcaption>Commercial bathroom partitions for new construction, renovation, and replacement.</figcaption>
        </figure>
      </section>

      <section className="product-choice product-choice--concise section-line" aria-labelledby="product-choice-title">
        <div className="simple-section-heading simple-section-heading--split">
          <div><span className="kicker">Start here</span><h2 id="product-choice-title">What does the restroom need?</h2></div>
          <p>Partitions, accessories, or both. We organize the material, mounting, hardware, and product details.</p>
        </div>
        <div className="product-choice__grid">
          <Link href="/bathroom-partitions#materials" className="product-choice__card">
            <div className="product-choice__media product-choice__media--partitions">
              <img className="product-choice__image" src={realPhotos.homepageCategories.partitions.large} srcSet={`${realPhotos.homepageCategories.partitions.small} 720w, ${realPhotos.homepageCategories.partitions.large} 900w`} sizes="(max-width: 560px) calc(100vw - 2rem), (max-width: 820px) 38vw, 19vw" width="900" height="900" alt="Stainless steel commercial bathroom partition door and panel system on a white background" loading="lazy" decoding="async" />
            </div>
            <div className="product-choice__copy"><span>01</span><h3>Bathroom Partitions</h3><p>Compare five materials, four mounting styles, colors, and hardware for commercial restrooms.</p><strong>Choose partitions <ArrowRight aria-hidden="true" /></strong></div>
          </Link>
          <Link href="/commercial-restroom-accessories" className="product-choice__card">
            <div className="product-choice__media product-choice__media--accessories">
              <img className="product-choice__image" src={realPhotos.homepageCategories.accessories.large} srcSet={`${realPhotos.homepageCategories.accessories.small} 720w, ${realPhotos.homepageCategories.accessories.large} 900w`} sizes="(max-width: 560px) calc(100vw - 2rem), (max-width: 820px) 38vw, 19vw" width="900" height="900" alt="Commercial restroom accessories including grab bars, dispensers, hand drying, and a baby changing station" loading="lazy" decoding="async" />
            </div>
            <div className="product-choice__copy"><span>02</span><h3>Bathroom Accessories</h3><p>Browse dispensers, grab bars, mirrors, dryers, waste units, and more.</p><strong>Browse accessories <ArrowRight aria-hidden="true" /></strong></div>
          </Link>
        </div>
      </section>

      <section className="simple-materials simple-materials--concise section-line" aria-labelledby="materials-title">
        <div className="simple-section-heading simple-section-heading--split">
          <div><span className="kicker">Five material choices</span><h2 id="materials-title">Pick the right partition material.</h2></div>
          <p>Compare cost, moisture resistance, traffic, appearance, and maintenance—or ask us to narrow the options.</p>
        </div>
        <div className="photo-material-grid">
          {materials.map((material) => {
            const photo = realPhotos.materials[material.slug as keyof typeof realPhotos.materials];
            return (
              <Link href={`/bathroom-partitions/${material.slug}`} className="photo-material-card" key={material.slug}>
                <img src={photo.large} srcSet={`${photo.small} 720w, ${photo.large} 1280w`} sizes="(max-width: 640px) 100vw, (max-width: 1100px) 50vw, 33vw" width="1280" height="858" alt={`${material.name} commercial bathroom partitions`} loading="lazy" />
                <div><span>{material.number}</span><h3>{material.name}</h3><p>{material.summary}</p><strong>View material <ArrowRight aria-hidden="true" /></strong></div>
              </Link>
            );
          })}
        </div>
        <div className="section-action"><Link href="/bathroom-partitions" className="button button--outline">Compare Materials & Mounting <ArrowRight aria-hidden="true" /></Link></div>
      </section>

      <section className="simple-accessories simple-accessories--concise section-line" aria-labelledby="accessories-title">
        <div className="simple-section-heading simple-section-heading--split">
          <div><span className="kicker">Commercial restroom accessories</span><h2 id="accessories-title">Complete the room.</h2></div>
          <div><p>Browse 15 image-led product categories and the manufacturers we carry.</p><Link href="/commercial-restroom-accessories" className="text-link">View accessories and brands <ArrowRight aria-hidden="true" /></Link></div>
        </div>
        <div className="accessory-photo-grid">
          {accessoryHighlights.map((item) => <Link href={item.href} key={item.name}><img src={item.photo.large} srcSet={`${item.photo.small} 720w, ${item.photo.large} 1280w`} sizes="(max-width: 640px) 100vw, 25vw" width="768" height="512" alt={item.alt} loading="lazy" /><span>{item.name}</span></Link>)}
        </div>
      </section>

      <section className="simple-process simple-process--concise section-line" aria-labelledby="process-title">
        <div className="simple-section-heading simple-section-heading--split">
          <div><span className="kicker">A simple quote process</span><h2 id="process-title">From project details to a focused quote.</h2></div>
          <p>You do not need every decision finalized. Start with the plans, dimensions, photos, quantities, or product schedule you already have.</p>
        </div>
        <ol>
          <li><span>01</span><h3>Send what you know</h3><p>Plans, measurements, photos, a written list, or a product schedule.</p></li>
          <li><span>02</span><h3>Confirm the scope</h3><p>Material, mounting, layout, hardware, colors, accessories, and delivery.</p></li>
          <li><span>03</span><h3>Receive the quote</h3><p>One organized commercial restroom package built around the project.</p></li>
        </ol>
        <div className="section-action"><Link href="/request-a-quote" className="button button--primary">Start Your Quote <ArrowUpRight aria-hidden="true" /></Link></div>
      </section>

      <GoogleReviewsCarousel />
    </>
  );
}
